import { useCallback, useEffect, useRef, useState } from 'react';

import { hasNewVersionDeployed, hasUserTyped, isVersionCheckSupported, watchUserInput } from 'helpers/appVersion';

/** Rythme de vérification tant que l'onglet est au premier plan. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Au-delà de cette durée passée en arrière-plan, on considère que l'utilisateur a quitté la page :
 * on la recharge pour lui au retour plutôt que de lui demander. En deçà, il est en train de
 * travailler et un rechargement subi lui coûterait sa saisie — c'est le bandeau qui prend le
 * relais.
 */
const BACKGROUND_RELOAD_THRESHOLD_MS = 30 * 60 * 1000;

type AppVersion = {
    isUpdateAvailable: boolean;
    reload: () => void;
};

/**
 * Surveille la version déployée et signale à l'onglet qu'il est périmé.
 *
 * Vérifie au montage, toutes les 5 minutes, et à chaque retour au premier plan — de loin le
 * déclencheur le plus rentable sur un back-office, où l'onglet reste ouvert en fond des heures.
 */
export function useAppVersion(): AppVersion {
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
    const isUpdateAvailableRef = useRef(false);
    const hiddenSinceRef = useRef<number | null>(null);

    useEffect(() => {
        if (!isVersionCheckSupported()) {
            return;
        }

        let isCancelled = false;

        const unwatchUserInput = watchUserInput();

        const check = async (): Promise<boolean> => {
            // Une fois la nouvelle version repérée, il n'y a plus rien à apprendre : on arrête de
            // demander. Un onglet caché n'a rien à afficher non plus.
            if (isUpdateAvailableRef.current) {
                return true;
            }

            if (document.hidden) {
                return false;
            }

            const hasUpdate = await hasNewVersionDeployed();

            if (!hasUpdate || isCancelled) {
                return false;
            }

            isUpdateAvailableRef.current = true;
            setIsUpdateAvailable(true);

            return true;
        };

        const onVisibilityChange = async () => {
            if (document.hidden) {
                hiddenSinceRef.current = Date.now();

                return;
            }

            const hiddenSince = hiddenSinceRef.current;
            hiddenSinceRef.current = null;

            const hasUpdate = await check();
            const hiddenForMs = hiddenSince === null ? 0 : Date.now() - hiddenSince;

            if (hasUpdate && hiddenForMs >= BACKGROUND_RELOAD_THRESHOLD_MS && !hasUserTyped()) {
                window.location.reload();
            }
        };

        void check();

        const intervalId = window.setInterval(() => {
            void check();
        }, POLL_INTERVAL_MS);

        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            isCancelled = true;
            unwatchUserInput();
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);

    const reload = useCallback(() => {
        window.location.reload();
    }, []);

    return { isUpdateAvailable, reload };
}
